import {useMemo} from "react";
import {useQueryClient} from "@tanstack/react-query";
import {useSnackbar} from "notistack";
import {useApiQuery} from "../api/hooks";
import apiClient from "../api/apiClient";
import {usePreloaderStore} from "../stores";

const FAIR_STATUS = {
    NOT_STARTED: 'NOT_STARTED',
    DEVELOP: 'DEVELOP',
    ACTIVE: 'ACTIVE',
    FINISHED: 'FINISHED'
};

const FAIR_STATUS_LABELS = {
    [FAIR_STATUS.NOT_STARTED]: 'Не начата',
    [FAIR_STATUS.DEVELOP]: 'Подготовка',
    [FAIR_STATUS.ACTIVE]: 'Активна',
    [FAIR_STATUS.FINISHED]: 'Завершена'
};

const POINT_TYPE = {
    SERVICE: 'SERVICE',
    PRODUCT: 'PRODUCT',
    CHANCE: 'CHANCE'
};

const POINT_TYPE_LABELS = {
    [POINT_TYPE.SERVICE]: 'Услуга',
    [POINT_TYPE.PRODUCT]: 'Товар',
    [POINT_TYPE.CHANCE]: 'Азартная игра'
};

const POINT_STATUS = {
    PENDING: 'PENDING',
    APPROVED: 'APPROVED',
    CHANGES_REQUESTED: 'CHANGES_REQUESTED'
};

const POINT_STATUS_LABELS = {
    [POINT_STATUS.PENDING]: 'На модерации',
    [POINT_STATUS.APPROVED]: 'Одобрена',
    [POINT_STATUS.CHANGES_REQUESTED]: 'Требует доработки'
};

const ALLOWED_PRICES = [50, 100, 250, 500, 750, 1000, 1250, 1500, 1750, 2000];
const ALLOWED_COEFFICIENTS = [1.1, 1.2, 1.3, 1.4, 1.5, 1.75, 2, 2.5, 3, 4, 5, 6, 7, 8, 9, 10];

export {
    FAIR_STATUS, FAIR_STATUS_LABELS,
    POINT_TYPE, POINT_TYPE_LABELS,
    POINT_STATUS, POINT_STATUS_LABELS,
    ALLOWED_PRICES, ALLOWED_COEFFICIENTS
};

/**
 * Централизованный хук для управления состоянием страницы FairEdit.
 * Содержит данные о командах ярмарки и методы для работы с точками, площадками и модерацией.
 */
export default function useFairEditState(fairId, initialFair = null) {
    const queryClient = useQueryClient();
    const {enqueueSnackbar} = useSnackbar();
    const {show: showPreloader, hide: hidePreloader} = usePreloaderStore();

    // ============ Query Keys ============
    const queryKeys = {
        game: ['game', fairId],
        pendingPoints: ['fair-points', 'pending', fairId],
        subsidyTransactions: ['fair-transactions', 'subsidies', fairId],
    };

    // ============ Queries ============
    const {data: gameData, isLoading: gameLoading} = useApiQuery(
        queryKeys.game,
        `/api/fair/admin/game/find-by-smena?smenaId=${initialFair?.smenaId}`,
        {refetchOnMount: 'always', enabled: !!initialFair?.smenaId, initialData: initialFair}
    );

    const {data: pendingPointsData, isLoading: pendingPointsLoading} = useApiQuery(
        queryKeys.pendingPoints,
        `/api/fair/admin/point/list-pending?gameId=${fairId}`,
        {refetchOnMount: 'always', enabled: !!fairId}
    );

    const {data: subsidyTransactionsData, isLoading: subsidyTransactionsLoading} = useApiQuery(
        queryKeys.subsidyTransactions,
        `/api/fair/admin/transaction/list-with-subsidies?gameId=${fairId}`,
        {refetchOnMount: 'always', enabled: !!fairId}
    );

    // ============ Computed Data ============
    const game = gameData || initialFair;

    const teams = useMemo(() => {
        if (!gameData?.teams) return [];
        return [...gameData.teams].sort((a, b) => (a.teamName || '').localeCompare(b.teamName || ''));
    }, [gameData]);

    const tradingAreas = useMemo(() => {
        if (!gameData?.teams) return [];
        const areas = gameData.teams.flatMap(t => t.tradingAreas || []);

        // Добавляем название команды-владельца по teamStateId
        return areas.map(area => {
            const ownerTeam = gameData.teams.find(t => t.id === area.teamStateId);
            return {
                ...area,
                teamName: ownerTeam?.teamName || null
            };
        }).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }, [gameData]);

    const pendingPoints = useMemo(() => {
        if (!pendingPointsData) return [];
        return pendingPointsData;
    }, [pendingPointsData]);

    const subsidyTransactions = useMemo(() => {
        if (!subsidyTransactionsData) return [];
        // Фильтруем транзакции с субсидиями, исключаем EXPIRED; новые сверху
        return subsidyTransactionsData
            .filter(t => t.hasSubsidy && t.status !== 'EXPIRED')
            .sort((a, b) => new Date(b.created) - new Date(a.created));
    }, [subsidyTransactionsData]);

    const centralBank = game?.centralBank || null;
    const centralBankBalance = centralBank?.balance || 0;

    // Статистика по субсидиям (только COMPLETED)
    const subsidyStats = useMemo(() => {
        const completedTransactions = subsidyTransactions.filter(t => t.status === 'COMPLETED');

        if (!completedTransactions.length) {
            return {
                count: 0,
                totalAmount: 0,
                averageAmount: 0
            };
        }

        const totalAmount = completedTransactions.reduce((sum, t) => {
            return sum + (t.subsidy?.amount || 0);
        }, 0);

        return {
            count: completedTransactions.length,
            totalAmount,
            averageAmount: Math.round(totalAmount / completedTransactions.length)
        };
    }, [subsidyTransactions]);

    // ============ Invalidation Methods ============
    const invalidateGame = () => {
        queryClient.invalidateQueries({queryKey: queryKeys.game});
    };

    const invalidatePendingPoints = () => {
        queryClient.invalidateQueries({queryKey: queryKeys.pendingPoints});
    };

    const invalidateSubsidyTransactions = () => {
        queryClient.invalidateQueries({queryKey: queryKeys.subsidyTransactions});
    };

    const invalidateAll = () => {
        invalidateGame();
        invalidatePendingPoints();
        invalidateSubsidyTransactions();
    };

    // ============ Game Status Actions ============
    const updateStatus = async (status) => {
        showPreloader();
        try {
            await apiClient.put(`/api/fair/admin/game/update-status?id=${fairId}&status=${status}`);
            invalidateGame();
            enqueueSnackbar(`Статус изменён на "${FAIR_STATUS_LABELS[status]}"`, {variant: "success"});
        } catch (error) {
            enqueueSnackbar("Ошибка при изменении статуса", {variant: "error"});
            throw error;
        } finally {
            hidePreloader();
        }
    };

    // ============ Teams Actions ============
    const refreshTeams = async () => {
        showPreloader();
        try {
            const response = await apiClient.post(`/api/fair/admin/team-state/refresh?gameId=${fairId}`);
            invalidateGame();
            enqueueSnackbar(`Команды обновлены (добавлено: ${response.data?.createdTeams || 0})`, {variant: "success"});
        } catch (error) {
            enqueueSnackbar("Ошибка при обновлении команд", {variant: "error"});
            throw error;
        } finally {
            hidePreloader();
        }
    };

    const deleteTeam = async (stateId) => {
        showPreloader();
        try {
            await apiClient.delete(`/api/fair/admin/team-state/delete?stateId=${stateId}`);
            invalidateGame();
            enqueueSnackbar("Команда удалена из игры", {variant: "success"});
        } catch (error) {
            enqueueSnackbar("Ошибка при удалении команды", {variant: "error"});
            throw error;
        } finally {
            hidePreloader();
        }
    };

    // ============ Balance Actions ============
    const addBalance = async (teamId, amount) => {
        showPreloader();
        try {
            await apiClient.post(
                `/api/fair/admin/team-state/add-balance?id=${teamId}`,
                {amount}
            );
            invalidateGame();
            enqueueSnackbar("Баланс добавлен", {variant: "success"});
        } catch (error) {
            enqueueSnackbar("Ошибка при добавлении баланса", {variant: "error"});
            throw error;
        } finally {
            hidePreloader();
        }
    };

    const addCentralBankBalance = async (amount) => {
        showPreloader();
        try {
            await apiClient.post(
                `/api/fair/admin/central-bank/add-balance?gameId=${fairId}&amount=${amount}`
            );
            invalidateGame();
            enqueueSnackbar("Баланс центрального банка обновлен", {variant: "success"});
        } catch (error) {
            enqueueSnackbar("Ошибка при обновлении баланса ЦБ", {variant: "error"});
            throw error;
        } finally {
            hidePreloader();
        }
    };

    // ============ Points Actions ============
    const createPointOnArea = async (tradingAreaId) => {
        showPreloader();
        try {
            await apiClient.post('/api/fair/admin/point/create', {
                tradingAreaId
            });
            invalidateGame();
            enqueueSnackbar("Точка создана", {variant: "success"});
        } catch (error) {
            enqueueSnackbar("Ошибка при создании точки", {variant: "error"});
            throw error;
        } finally {
            hidePreloader();
        }
    };

    const savePoint = async (pointId, pointData, newFiles = []) => {
        showPreloader();
        try {
            const formData = new FormData();

            const mediaList = [
                ...(pointData.mediaList || []).map((m, idx) => ({id: m.id, orderId: idx})),
                ...newFiles.map((_, idx) => ({id: null, orderId: (pointData.mediaList?.length || 0) + idx}))
            ];

            formData.append('point', new Blob([JSON.stringify({
                name: pointData.name,
                description: pointData.description,
                code: pointData.code,
                type: pointData.type,
                price: pointData.price,
                winCoefficient: pointData.winCoefficient,
                maxVisitors: pointData.maxVisitors,
                serviceDuration: pointData.serviceDuration,
                mediaList
            })], {type: 'application/json'}));

            newFiles.forEach(file => {
                formData.append('newFiles', file);
            });

            const response = await apiClient.put(
                `/api/fair/admin/point/save?id=${pointId}`,
                formData,
                {headers: {'Content-Type': 'multipart/form-data'}}
            );
            invalidateGame();
            invalidatePendingPoints();
            enqueueSnackbar("Сохранено", {variant: "success"});
            return response.data;
        } catch (error) {
            enqueueSnackbar("Ошибка при сохранении точки", {variant: "error"});
            throw error;
        } finally {
            hidePreloader();
        }
    };

    const deletePoint = async (pointId) => {
        showPreloader();
        try {
            await apiClient.delete(`/api/fair/admin/point/delete?id=${pointId}`);
            invalidateGame();
            enqueueSnackbar("Точка удалена", {variant: "success"});
        } catch (error) {
            enqueueSnackbar("Ошибка при удалении точки", {variant: "error"});
            throw error;
        } finally {
            hidePreloader();
        }
    };

    // ============ Trading Areas Actions ============
    const createTradingArea = async (name, smenaId) => {
        showPreloader();
        try {
            const response = await apiClient.post('/api/fair/admin/trading-area/create', {
                name,
                smenaId
            });
            invalidateGame();
            enqueueSnackbar("Площадка создана", {variant: "success"});
            return response.data;
        } catch (error) {
            enqueueSnackbar("Ошибка при создании площадки", {variant: "error"});
            throw error;
        } finally {
            hidePreloader();
        }
    };

    const updateTradingArea = async (areaId, data) => {
        showPreloader();
        try {
            await apiClient.put(`/api/fair/admin/trading-area/update?id=${areaId}`, data);
            invalidateGame();
            enqueueSnackbar("Площадка обновлена", {variant: "success"});
        } catch (error) {
            enqueueSnackbar("Ошибка при обновлении площадки", {variant: "error"});
            throw error;
        } finally {
            hidePreloader();
        }
    };

    const deleteTradingArea = async (areaId) => {
        showPreloader();
        try {
            await apiClient.delete(`/api/fair/admin/trading-area/delete?id=${areaId}`);
            invalidateGame();
            enqueueSnackbar("Площадка удалена", {variant: "success"});
        } catch (error) {
            enqueueSnackbar("Ошибка при удалении площадки", {variant: "error"});
            throw error;
        } finally {
            hidePreloader();
        }
    };

    // ============ Moderation Actions ============
    const approvePoint = async (pointId) => {
        showPreloader();
        try {
            await apiClient.post(`/api/fair/admin/point/approve?id=${pointId}`);
            invalidatePendingPoints();
            invalidateGame();
            enqueueSnackbar("Точка одобрена", {variant: "success"});
        } catch (error) {
            enqueueSnackbar("Ошибка при одобрении точки", {variant: "error"});
            throw error;
        } finally {
            hidePreloader();
        }
    };

    const requestChanges = async (pointId, comment) => {
        showPreloader();
        try {
            await apiClient.post(`/api/fair/admin/point/request-changes?id=${pointId}&comment=${encodeURIComponent(comment)}`);
            invalidatePendingPoints();
            invalidateGame();
            enqueueSnackbar("Запрос на доработку отправлен", {variant: "success"});
        } catch (error) {
            enqueueSnackbar("Ошибка при отправке на доработку", {variant: "error"});
            throw error;
        } finally {
            hidePreloader();
        }
    };

    // ============ QR Codes ============
    const downloadAllQrCodes = async () => {
        showPreloader();
        try {
            const response = await apiClient.get(
                `/api/fair/admin/point/export-qr-codes?gameId=${fairId}`,
                {responseType: 'blob'}
            );
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `qr-codes-game-${fairId}.zip`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            enqueueSnackbar("Все QR-коды скачаны", {variant: "success"});
        } catch (error) {
            // Тело ошибки приходит как Blob (responseType: 'blob') — достаём реальное сообщение бэка.
            let message = "Ошибка при скачивании QR-кодов";
            try {
                const data = error.response?.data;
                const text = data instanceof Blob ? await data.text() : null;
                const parsed = text ? JSON.parse(text) : data;
                message = parsed?.message || message;
            } catch { /* оставляем дефолтное сообщение */ }
            enqueueSnackbar(message, {variant: "error"});
            throw error;
        } finally {
            hidePreloader();
        }
    };

    return {
        // Data
        game,
        teams,
        tradingAreas,
        pendingPoints,
        subsidyTransactions,
        centralBankBalance,
        subsidyStats,

        // Loading states
        isLoading: gameLoading,
        gameLoading,
        pendingPointsLoading,
        subsidyTransactionsLoading,

        // Game status actions
        updateStatus,

        // Teams actions
        refreshTeams,
        deleteTeam,

        // Balance actions
        addBalance,
        addCentralBankBalance,

        // Points actions
        createPointOnArea,
        savePoint,
        deletePoint,

        // Trading Areas actions
        createTradingArea,
        updateTradingArea,
        deleteTradingArea,

        // Moderation actions
        approvePoint,
        requestChanges,

        // QR codes
        downloadAllQrCodes,

        // Invalidation
        invalidateAll,
        invalidateGame,
        invalidatePendingPoints,
    };
}
